import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Typography,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Stack,
    Divider,
    Alert,
    Paper,
    Grid,
    Fade,
    Slide
} from "@mui/material";
import {
    Close as CloseIcon,
    Email as EmailIcon,
    WhatsApp as WhatsAppIcon,
    Event as CalendarIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    Info as InfoIcon,
    CheckCircle as CheckCircleIcon,
    ArrowForward as ArrowForwardIcon,
    ContactSupport as SupportIcon,
    AccessTime as TimeIcon,
    Refresh as RefreshIcon
} from "@mui/icons-material";
import Cookies from "js-cookie";
import { useAuth } from "../../context/AuthContext";
import { orange } from "@mui/material/colors";

const SubscriptionNotification = () => {
    const [open, setOpen] = useState(false);
    const [subscriptions, setSubscriptions] = useState({
        businessEmail: null,
        personalWhatsapp: null,
        businessWhatsapp: null
    });

    const { subscriptionStatus } = useAuth();

    useEffect(()=>{
        subscriptionStatus();
    },[]);

    // Get subscription dates from cookies
    const businessEmailSubscriptionEnd = Cookies.get("bese");
    const personalWhatsappSubscriptionEnd = Cookies.get("pwse");
    const businessWhatsappSubscriptionEnd = Cookies.get("bwse");

    // Format date function
    const formatDate = (dateString) => {
        if (!dateString) return "Not available";
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return "Invalid date";
        }
    };

    // Format short date
    const formatShortDate = (dateString) => {
        if (!dateString) return "N/A";
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
        } catch (error) {
            return "N/A";
        }
    };

    // Calculate days remaining
    const calculateDaysRemaining = (dateString) => {
        if (!dateString) return null;
        try {
            const endDate = new Date(dateString);
            const today = new Date();
            const diffTime = endDate - today;
            return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        } catch (error) {
            return null;
        }
    };

    // Get subscription status
    const getSubscriptionStatus = (dateString, type) => {
        const daysRemaining = calculateDaysRemaining(dateString);
        
        if (daysRemaining === null) {
            return {
                status: 'no-data',
                message: 'No subscription data available',
                color: 'default',
                icon: <InfoIcon />,
                severity: 'info'
            };
        }
        
        if (daysRemaining <= 0) {
            return {
                status: 'expired',
                message: 'Subscription has expired',
                color: 'error',
                icon: <ErrorIcon />,
                severity: 'error',
                days: daysRemaining
            };
        } else if (daysRemaining <= 3) {
            return {
                status: 'critical',
                message: 'Expiring soon',
                color: 'warning',
                icon: <WarningIcon />,
                severity: 'warning',
                days: daysRemaining
            };
        } else if (daysRemaining <= 7) {
            return {
                status: 'warning',
                message: 'Expiring next week',
                color: 'warning',
                icon: <WarningIcon />,
                severity: 'warning',
                days: daysRemaining
            };
        } else if (daysRemaining <= 30) {
            return {
                status: 'info',
                message: 'Expiring this month',
                color: 'info',
                icon: <InfoIcon />,
                severity: 'info',
                days: daysRemaining
            };
        } else {
            return {
                status: 'active',
                message: 'Active',
                color: 'success',
                icon: <CheckCircleIcon />,
                severity: 'success',
                days: daysRemaining
            };
        }
    };

    // Check subscriptions and prepare data
    useEffect(() => {
        const businessStatus = getSubscriptionStatus(businessEmailSubscriptionEnd, 'business');
        const whatsappStatus = getSubscriptionStatus(personalWhatsappSubscriptionEnd, 'whatsapp');
        const businessWhatsappStatus = getSubscriptionStatus(businessWhatsappSubscriptionEnd, 'businessWhatsapp');

        setSubscriptions({
            businessEmail: {
                type: 'Business Email',
                icon: <EmailIcon sx={{ fontSize: 28 }} />,
                endDate: businessEmailSubscriptionEnd,
                formattedDate: formatDate(businessEmailSubscriptionEnd),
                shortDate: formatShortDate(businessEmailSubscriptionEnd),
                status: businessStatus,
                description: 'Send bulk emails, track analytics, and manage email campaigns'
            },
            personalWhatsapp: {
                type: 'Personal WhatsApp',
                icon: <WhatsAppIcon sx={{ fontSize: 28 }} />,
                endDate: personalWhatsappSubscriptionEnd,
                formattedDate: formatDate(personalWhatsappSubscriptionEnd),
                shortDate: formatShortDate(personalWhatsappSubscriptionEnd),
                status: whatsappStatus,
                description: 'Send WhatsApp messages, manage contacts, and track delivery'
            },
            businessWhatsapp: {
                type: 'Business WhatsApp',
                icon: <WhatsAppIcon sx={{ fontSize: 28 }} />,
                endDate: businessWhatsappSubscriptionEnd,
                formattedDate: formatDate(businessWhatsappSubscriptionEnd),
                shortDate: formatShortDate(businessWhatsappSubscriptionEnd),
                status: businessWhatsappStatus,
                description: 'Send WhatsApp Business messages, manage campaigns, and track analytics'
            }
        });

        // Open modal if any subscription is expired or critical (≤3 days)
        if (businessStatus.severity === 'error' || businessStatus.severity === 'warning' ||
            whatsappStatus.severity === 'error' || whatsappStatus.severity === 'warning' ||
            businessWhatsappStatus.severity === 'error' || businessWhatsappStatus.severity === 'warning') {
            setOpen(true);
        }
    }, [businessEmailSubscriptionEnd, personalWhatsappSubscriptionEnd, businessWhatsappSubscriptionEnd]);

    const handleClose = () => {
        setOpen(false);
    };
const handleContactSupport = () => {
    const phoneNumber = "917678134697"; // Include country code, no + sign
    const message = "Hello, I need support.";

    const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank");
};

    const handleRefresh = () => {
        window.location.reload();
    };

    const SubscriptionCard = ({ subscription, type }) => {
        if (!subscription) return null;

        const { status, icon, color } = subscription.status;
        
        return (
            <Fade in={true} timeout={500}>
                <Card 
                    elevation={2}
                    sx={{
                        height: '100%',
                        border: `1px solid`,
                        borderColor: status === 'expired' ? 'error.light' : 
                                   status === 'critical' ? 'warning.light' : 'divider',
                        borderRadius: 2,
                        transition: 'all 0.3s',
                        '&:hover': {
                            boxShadow: 4,
                            transform: 'translateY(-2px)'
                        }
                    }}
                >
                    <CardContent sx={{ p: 3 }}>
                        {/* Header */}
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                width: 48,
                                height: 48,
                                borderRadius: 2,
                                // bgcolor: `${color}.light`,
                                color: `${color}.main`,
                                mr: 2
                            }}>
                                {subscription.icon}
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="h6" fontWeight={600}>
                                    {subscription.type}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {subscription.description}
                                </Typography>
                            </Box>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {/* Status Badge */}
                        <Box sx={{ mb: 2 }}>
                            <Chip
                                icon={icon}
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2" fontWeight={500}>
                                            {subscription.status.message}
                                        </Typography>
                                        {subscription.status.days !== undefined && subscription.status.days > 0 && (
                                            <Typography variant="caption" sx={{ ml: 0.5 }}>
                                                ({subscription.status.days} days)
                                            </Typography>
                                        )}
                                    </Box>
                                }
                                color={color}
                                variant="filled"
                                sx={{ 
                                    fontWeight: 600,
                                    px: 1,
                                    py: 0.5,
                                    height: 'auto'
                                }}
                            />
                        </Box>

                        {/* Date Information */}
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            p: 1.5,
                            bgcolor: 'grey.50',
                            borderRadius: 1,
                            mb: 2
                        }}>
                            <CalendarIcon sx={{ mr: 1.5, color: 'text.secondary', fontSize: 20 }} />
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Subscription End Date
                                </Typography>
                                <Typography variant="body2" fontWeight={500}>
                                    {subscription.formattedDate}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Status-specific message */}
                        {status === 'expired' && (
                            <Alert 
                                severity="error" 
                                icon={<ErrorIcon />}
                                sx={{ 
                                    mb: 2,
                                    borderRadius: 1,
                                    '& .MuiAlert-icon': { alignItems: 'center' }
                                }}
                            >
                                <Typography variant="body2" fontWeight={500}>
                                    Service is currently unavailable. Please renew immediately.
                                </Typography>
                            </Alert>
                        )}
                        
                        {status === 'critical' && (
                            <Alert 
                                severity="warning" 
                                icon={<WarningIcon />}
                                sx={{ 
                                    mb: 2,
                                    borderRadius: 1,
                                    '& .MuiAlert-icon': { alignItems: 'center' }
                                }}
                            >
                                <Typography variant="body2" fontWeight={500}>
                                    Service will stop working soon. Renew before expiration.
                                </Typography>
                            </Alert>
                        )}
                    </CardContent>
                </Card>
            </Fade>
        );
    };

    const getOverallStatus = () => {
        const businessStatus = subscriptions.businessEmail?.status;
        const whatsappStatus = subscriptions.personalWhatsapp?.status;
        const businessWhatsappStatus = subscriptions.businessWhatsapp?.status;
        
        if (!businessStatus && !whatsappStatus && !businessWhatsappStatus) return 'no-data';
        
        if ((businessStatus?.status === 'expired') || (whatsappStatus?.status === 'expired') || (businessWhatsappStatus?.status === 'expired')) {
            return 'critical';
        }
        if ((businessStatus?.status === 'critical') || (whatsappStatus?.status === 'critical') || (businessWhatsappStatus?.status === 'critical')) {
            return 'warning';
        }
        if ((businessStatus?.status === 'warning') || (whatsappStatus?.status === 'warning') || (businessWhatsappStatus?.status === 'warning')) {
            return 'info';
        }
        if ((businessStatus?.status === 'info') || (whatsappStatus?.status === 'info') || (businessWhatsappStatus?.status === 'info')) {
            return 'info';
        }
        return 'active';
    };

    const overallStatus = getOverallStatus();
    const isCritical = overallStatus === 'critical' || overallStatus === 'warning';

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                    background: 'linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%)'
                }
            }}
            TransitionComponent={Slide}
            TransitionProps={{ direction: 'up' }}
        >
            {/* Header */}
            <DialogTitle sx={{ 
                p: 3, 
                pb: 2,
                background: isCritical 
                    ? 'linear-gradient(135deg, #ff6b6b 0%, #ff8e53 100%)'
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white'
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {isCritical ? (
                            <WarningIcon sx={{ fontSize: 32 }} />
                        ) : (
                            <InfoIcon sx={{ fontSize: 32 }} />
                        )}
                        <Box>
                            <Typography variant="h5" fontWeight={700}>
                                Subscription Status
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                                {isCritical ? 'Action Required' : 'Current Subscription Status'}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton 
                        onClick={handleClose}
                        sx={{ 
                            color: 'white',
                            bgcolor: 'rgba(255,255,255,0.2)',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            {/* Content */}
            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ p: 4 }}>
                    {/* Summary Alert */}
                    {isCritical && (
                        <Alert 
                            severity="warning"
                            icon={<WarningIcon fontSize="large" />}
                            sx={{ 
                                mb: 4,
                                borderRadius: 2,
                                border: '2px solid',
                                borderColor: 'warning.main',
                                bgcolor: orange[50],
                                '& .MuiAlert-message': { flex: 1 }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h6" fontWeight={600} gutterBottom>
                                        ⚠️ Immediate Action Required
                                    </Typography>
                                    <Typography variant="body1">
                                        {overallStatus === 'critical' 
                                            ? 'One or more subscriptions have expired! Services may be disrupted.'
                                            : 'One or more subscriptions are expiring soon. Renew to avoid service interruption.'}
                                    </Typography>
                                </Box>
                                {/* <Button
                                    variant="contained"
                                    color="warning"
                                    endIcon={<ArrowForwardIcon />}
                                    onClick={handleContactSupport}
                                    sx={{ fontWeight: 600, px: 3 }}
                                >
                                    Renew Now
                                </Button> */}
                            </Box>
                        </Alert>
                    )}

                    {/* Welcome/Info Message */}
                    {/* <Box sx={{ 
                        mb: 4, 
                        p: 3, 
                        bgcolor: 'primary.light', 
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'primary.main'
                    }}>
                        <Typography variant="body1" color="primary.contrastText" sx={{ mb: 1 }}>
                            👋 Welcome to your subscription dashboard
                        </Typography>
                        <Typography variant="body2" color="primary.contrastText" sx={{ opacity: 0.9 }}>
                            Here you can view the current status of all your subscriptions. 
                            {isCritical ? ' Please take immediate action for any expiring or expired services.' : ' All services are currently active.'}
                        </Typography>
                    </Box> */}

                    {/* Subscription Cards */}
                    <Grid container spacing={3}>
                        {subscriptions.businessEmail && (
                            <Grid item xs={12} md={6} lg={4}>
                                <SubscriptionCard 
                                    subscription={subscriptions.businessEmail} 
                                    type="business"
                                />
                            </Grid>
                        )}
                        {subscriptions.personalWhatsapp && (
                            <Grid item xs={12} md={6} lg={4}>
                                <SubscriptionCard 
                                    subscription={subscriptions.personalWhatsapp} 
                                    type="whatsapp"
                                />
                            </Grid>
                        )}
                        {subscriptions.businessWhatsapp && (
                            <Grid item xs={12} md={6} lg={4}>
                                <SubscriptionCard 
                                    subscription={subscriptions.businessWhatsapp} 
                                    type="businessWhatsapp"
                                />
                            </Grid>
                        )}
                    </Grid>

                    {/* Support Information */}
                    <Box sx={{ 
                        mt: 4, 
                        p: 3, 
                        bgcolor: 'grey.50', 
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <SupportIcon color="primary" sx={{ mr: 2, fontSize: 28 }} />
                            <Typography variant="h6" fontWeight={600}>
                                Need Help?
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Our support team is available 24/7 to assist you with subscription renewals, 
                            billing questions, or any other concerns you may have.
                        </Typography>
                        <Button
                            variant="outlined"
                            startIcon={<SupportIcon />}
                            onClick={handleContactSupport}
                            sx={{ mr: 2, fontWeight: 600 }}
                        >
                            Contact Support
                        </Button>
                        <Button
                            variant="text"
                            startIcon={<RefreshIcon />}
                            onClick={handleRefresh}
                        >
                            Refresh Status
                        </Button>
                    </Box>
                </Box>
            </DialogContent>

            {/* Footer */}
            <DialogActions sx={{ 
                p: 3, 
                pt: 2,
                bgcolor: 'grey.100',
                borderTop: '1px solid',
                borderColor: 'divider'
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TimeIcon fontSize="small" color="action" />
                        <Typography variant="caption" color="text.secondary">
                            Last updated: {new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                        </Typography>
                    </Box>
                    <Box>
                        <Button
                            variant="contained"
                            onClick={handleClose}
                            sx={{ 
                                fontWeight: 600,
                                px: 4,
                                bgcolor: isCritical ? 'warning.main' : 'primary.main',
                                '&:hover': {
                                    bgcolor: isCritical ? 'warning.dark' : 'primary.dark'
                                }
                            }}
                        >
                            {isCritical ? 'I Understand' : 'Close Dashboard'}
                        </Button>
                    </Box>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default SubscriptionNotification;